import './AgentChat.css'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import {
  agentConversationGetSpec,
  agentRunSpec,
  type AgentStreamEvent,
  type AgentTurnRow,
  type AgentWidget,
} from 'aevocam-rest-api-spec'
import { Button } from '@/components/action/Button'
import { TextArea } from '@/components/scalar/TextArea'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'
import { toastError } from '@/components/action/Toaster'
import { useApi } from '@/hooks/useApi'
import { AgentReply } from './AgentReply'

type ChatMessage = {
  id: number
  role: 'user' | 'agent'
  content: string
  widgets?: AgentWidget[]
  /** Ephemeral-then-kept model reasoning for this reply */
  reasoning?: string
  /** True only for the first render after widgets arrive via the live stream */
  fromStream?: boolean
}

let nextMessageId = 1

function conversationStorageKey(persistenceKey: string) {
  return `agentConversation.${persistenceKey}`
}

function turnReasoningText(turn: AgentTurnRow): string | undefined {
  const parts: string[] = []
  for (const record of turn.reasoning ?? []) {
    if (record.summary?.length)
      parts.push(...record.summary)
    else if (record.content?.length)
      parts.push(...record.content)
  }
  const text = parts.join('\n').trim()
  return text || undefined
}

function turnsToMessages(turns: AgentTurnRow[]): ChatMessage[] {
  const messages: ChatMessage[] = []
  for (const turn of turns) {
    if (turn.prompt)
      messages.push({ id: nextMessageId++, role: 'user', content: turn.prompt })

    const widgets: AgentWidget[] = []
    for (const tool of turn.tools ?? []) {
      if (tool.forUi)
        widgets.push(...tool.forUi)
    }

    const reasoning = turnReasoningText(turn)

    if (turn.reply)
      messages.push({ id: nextMessageId++, role: 'agent', content: turn.reply, widgets, reasoning })
    else if (turn.error)
      messages.push({ id: nextMessageId++, role: 'agent', content: `*Error:* ${turn.error}`, widgets, reasoning })
    else if (widgets.length > 0 || reasoning)
      messages.push({ id: nextMessageId++, role: 'agent', content: '', widgets, reasoning })
  }
  return messages
}

type Props = {
  agent: string
  width?: string | number
  context?: Record<string, unknown>
  persistenceKey?: string
  /** When agent wants to the article list to get custom filtered */
  setArticleIds?: (articleIds?: string[]) => void
  /** Fires when the article editor's contents need to be reloaded */
  onRefreshEditor?: () => void
  /** Fires after a streamed reply finishes (sending → idle) */
  onReplyDone?: () => void
  onHide?: () => void
}

export function AgentChat({
  agent,
  width = '30rem',
  context,
  persistenceKey,
  setArticleIds,
  onRefreshEditor,
  onReplyDone,
  onHide,
}: Props) {

  const api = useApi()

  const [prompt, setPrompt] = useState('')
  const [history, setHistory] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | undefined>(() => {
    if (!persistenceKey) return undefined
    return localStorage.getItem(conversationStorageKey(persistenceKey)) ?? undefined
  })
  const [sending, setSending] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(() => !!conversationId)
  const [autoScroll, setAutoScroll] = useState(true)

  const chatHistoryRef = useRef<HTMLDivElement>(null)
  const chatContentRef = useRef<HTMLDivElement>(null)
  const promptRowRef = useRef<HTMLDivElement>(null)
  const initialConversationIdRef = useRef(conversationId)
  const wasSendingRef = useRef(false)
  const autoScrollRef = useRef(autoScroll)
  const programmaticScrollRef = useRef(false)

  autoScrollRef.current = autoScroll

  //--------------------------------------------------------------------------------
  function scrollChatToBottom() {
    const el = chatHistoryRef.current
    if (!el) return
    programmaticScrollRef.current = true
    el.scrollTop = el.scrollHeight
    // Ignore scroll events from this adjustment (and a follow-up layout pass).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        programmaticScrollRef.current = false
      })
    })
  }

  //--------------------------------------------------------------------------------
  useEffect(() => {
    const id = initialConversationIdRef.current
    if (!id) return

    let cancelled = false

    async function loadHistory() {
      setLoadingHistory(true)
      try {
        const res = await api.request(agentConversationGetSpec, {
          path: { conversation: id! },
        })
        if (cancelled) return

        if (res.status !== 200 || !res.data?.turns) {
          setConversationId(undefined)
          toastError(res.error ?? 'Conversation not found')
          return
        }

        setHistory(turnsToMessages(res.data.turns.rows ?? []))
      } catch (error) {
        if (cancelled) return
        console.error(error)
        setConversationId(undefined)
        toastError('Failed to load conversation')
      } finally {
        if (!cancelled) setLoadingHistory(false)
      }
    }

    void loadHistory()
    return () => { cancelled = true }
  }, [api])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!autoScroll) return
    scrollChatToBottom()
  }, [history, autoScroll])

  //--------------------------------------------------------------------------------
  // Markdown/widgets can grow the reply after `history` settles; keep pinned while
  // auto-scroll is on.
  useEffect(() => {
    const content = chatContentRef.current
    if (!content) return

    const observer = new ResizeObserver(() => {
      if (autoScrollRef.current) scrollChatToBottom()
    })
    observer.observe(content)
    return () => observer.disconnect()
  }, [])

  //--------------------------------------------------------------------------------
  // After sending ends, flush reply-done once child widget effects have run
  // (they may have marked a pending editor refresh during fromStream).
  useEffect(() => {
    if (sending) {
      wasSendingRef.current = true
      return
    }
    if (!wasSendingRef.current) return
    wasSendingRef.current = false
    onReplyDone?.()
  }, [sending, onReplyDone])

  //--------------------------------------------------------------------------------
  function handleChatScroll() {
    if (programmaticScrollRef.current) return
    const el = chatHistoryRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
    setAutoScroll(nearBottom)
  }

  //--------------------------------------------------------------------------------
  function focusPrompt() {
    promptRowRef.current?.querySelector('textarea')?.focus()
  }

  //--------------------------------------------------------------------------------
  function handleNewConversation() {
    if (persistenceKey)
      localStorage.removeItem(conversationStorageKey(persistenceKey))
    setConversationId(undefined)
    setHistory([])
    setPrompt('')
    setAutoScroll(true)
    focusPrompt()
  }

  //--------------------------------------------------------------------------------
  function appendAgentContent(messageId: number, content: string) {
    setHistory(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, content: message.content + content }
        : message,
    ))
  }

  //--------------------------------------------------------------------------------
  function setAgentContent(messageId: number, content: string) {
    setHistory(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, content }
        : message,
    ))
  }

  //--------------------------------------------------------------------------------
  function appendAgentWidget(messageId: number, widget: AgentWidget) {
    setHistory(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, widgets: [...(message.widgets ?? []), widget] }
        : message,
    ))
  }

  function appendAgentReasoning(messageId: number, delta: string) {
    setHistory(prev => prev.map(message =>
      message.id === messageId
        ? { ...message, reasoning: (message.reasoning ?? '') + delta }
        : message,
    ))
  }

  //--------------------------------------------------------------------------------
  async function handleSend() {
    const text = prompt.trim()
    if (!text || sending || loadingHistory) return

    const agentMessageId = nextMessageId++
    const pendingWidgets: AgentWidget[] = []

    setHistory(prev => [
      ...prev,
      { id: nextMessageId++, role: 'user', content: text },
      { id: agentMessageId, role: 'agent', content: '' },
    ])
    setPrompt('')
    setSending(true)
    setAutoScroll(true)
    focusPrompt()

    try {
      const res = await api.requestStream(agentRunSpec, {
        path: { agent },
        body: {
          conversationId,
          input: text,
          context,
        },
        onEvent: (event: AgentStreamEvent) => {
          switch (event.type) {
            case 'turn.start':
              setConversationId(event.conversationId)
              if (persistenceKey)
                localStorage.setItem(conversationStorageKey(persistenceKey), event.conversationId)
              break
            case 'text.delta':
              appendAgentContent(agentMessageId, event.delta)
              break
            case 'turn.done':
              setConversationId(event.conversationId)
              setHistory(prev => prev.map(message =>
                message.id === agentMessageId
                  ? {
                      ...message,
                      content: event.reply || message.content,
                      ...(pendingWidgets.length > 0
                        ? { widgets: [...pendingWidgets], fromStream: true }
                        : {}),
                    }
                  : message,
              ))
              break
            case 'error':
              setAgentContent(agentMessageId, `*Error:* ${event.message}`)
              break
            case 'reasoning':
              if (event.status === 'in_progress')
                appendAgentReasoning(agentMessageId, event.summary)
              break
            case 'widget':
              pendingWidgets.push(event.widget)
              // Attach immediately so placeholder-matched widgets can render
              // while text is still streaming; trailing widgets wait in AgentReply.
              appendAgentWidget(agentMessageId, event.widget)
              break
          }
        },
      })

      if (res.status !== 200) {
        const message = res.error ?? 'Agent request failed'
        toastError(message)
        setAgentContent(agentMessageId, `*Error:* ${message}`)
      }
    } catch (error) {
      console.error(error)
      toastError('Agent request failed')
      setAgentContent(agentMessageId, '*Error:* Request failed')
    } finally {
      setSending(false)
    }
  }

  //--------------------------------------------------------------------------------
  function handlePromptKeyDown(ev: KeyboardEvent<HTMLTextAreaElement>) {
    if (ev.key !== 'Enter') return
    if (ev.shiftKey || ev.ctrlKey) return
    ev.preventDefault()
    void handleSend()
  }

  //--------------------------------------------------------------------------------
  return (
    <div
      className='AgentChat'
      style={{ width: '100%', maxWidth: width }}
    >

      <div className='Toolbar'>
        <Button
          label='New conversation'
          icon='plus'
          disabled={sending || loadingHistory || (!conversationId && history.length === 0)}
          onClick={handleNewConversation}
        />
        {onHide && (
          <Button
            label='Hide'
            icon='chevronRight'
            iconPosition='right'
            onClick={onHide}
          />
        )}
      </div>

      <div
        className='ChatHistory'
        ref={chatHistoryRef}
        onScroll={handleChatScroll}
      >
        <div className='Content' ref={chatContentRef}>
          {history.map(message => (
            <div
              key={message.id}
              className={`Message ${message.role === 'user' ? 'User' : 'Agent'}`}
            >
              {message.role === 'agent' ? (
                <AgentReply
                  content={message.content || (
                    sending && message.id === history[history.length - 1]?.id
                      ? '…'
                      : ''
                  )}
                  widgets={message.widgets}
                  reasoning={message.reasoning}
                  streaming={
                    sending && message.id === history[history.length - 1]?.id
                  }
                  reasoningStreaming={
                    sending
                    && message.id === history[history.length - 1]?.id
                    && !message.content
                  }
                  fromStream={message.fromStream}
                  onStreamConsumed={() => setHistory(prev => prev.map(entry =>
                    entry.id === message.id && entry.fromStream
                      ? { ...entry, fromStream: undefined }
                      : entry,
                  ))}
                  setArticleIds={setArticleIds}
                  onRefreshEditor={onRefreshEditor}
                />
              ) : (
                <div className='Bubble'>
                  <MarkdownViewer value={message.content} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className='PromptRow' ref={promptRowRef}>
        <TextArea
          label='Prompt'
          value={prompt}
          setValue={setPrompt}
          autoHeight
          rows={3}
          disabled={loadingHistory}
          onKeyDown={handlePromptKeyDown}
        />
      </div>

    </div>
  )
}
